import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import EyeOutlined from '@ant-design/icons/EyeOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import { Button, Checkbox, Tag } from 'antd'
import Table, { ColumnsType } from 'antd/es/table'
import { FfsLtaReport } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import { FC, useCallback, useState } from 'react'
import styled from 'styled-components'

import {
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
} from './ffsLtaPublishApi'

const NewReportButtonContainer = styled.div`
  text-align: center;
  padding: 16px 0;
`

export interface FfsResultTableProps {
  ffsReports: ReadonlyArray<FfsLtaReport>
  loading: boolean
  deleteReport?: (id: string) => Promise<void>
  newReport?: () => void
  setDefaultReport?: (id: string | undefined) => Promise<void>
  setSelectedScenarioId?: (id: string | null) => void
  selectedScenarioId?: string | null
}

const TagIconButton: FC<{
  icon: React.ReactNode
  checked?: boolean
  onClick: () => void
}> = ({ icon, checked, onClick }) => {
  return (
    <Tag.CheckableTag
      checked={checked ?? false}
      onClick={onClick}
      style={{ cursor: 'pointer' }}>
      {icon}
    </Tag.CheckableTag>
  )
}

export const FfsLtaReportTable: FC<FfsResultTableProps> = ({
  ffsReports,
  loading,
  deleteReport,
  newReport,
  setDefaultReport,
  setSelectedScenarioId,
  selectedScenarioId,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const handleSetDefaultReport = useCallback(
    (id: string | undefined) => {
      if (!setDefaultReport) return
      setIsSaving(true)
      setDefaultReport(id).finally(() => {
        setIsSaving(false)
      })
    },
    [setDefaultReport]
  )
  const handleDeleteReport = useCallback(
    (id: string) => {
      if (!deleteReport) return
      setIsSaving(true)
      deleteReport(id).finally(() => {
        setIsSaving(false)
      })
    },
    [deleteReport]
  )
  const handleSetSelectedScenarioId = useCallback(
    (id: string | null) => {
      if (!setSelectedScenarioId) return
      setSelectedScenarioId(id)
    },
    [setSelectedScenarioId]
  )
  const columns: ColumnsType<FfsLtaReport> = [
    {
      title: 'Default',
      dataIndex: 'is_default',
      render: (isDefault: boolean, record: FfsLtaReport) => (
        <Checkbox
          checked={isDefault}
          disabled={!setDefaultReport}
          onChange={(e) =>
            handleSetDefaultReport(e.target.checked ? record.id : undefined)
          }
        />
      ),
    },
    {
      title: 'Scenario',
      dataIndex: 'outcome',
      render: (outcome: FfsLtaReport['outcome']) => outcome.scenario,
    },
    {
      title: 'Result',
      dataIndex: 'outcome',
      render: (outcome: FfsLtaReport['outcome']) => (
        <Tag color={RESULT_OUTCOME_COLORS[outcome.result]}>
          {RESULT_OUTCOME_LABELS[outcome.result]}
        </Tag>
      ),
    },
    deleteReport || setSelectedScenarioId
      ? {
          dataIndex: 'id',
          render: (id: string) => (
            <>
              {deleteReport && (
                <TagIconButton
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteReport(id)}
                />
              )}
              {setSelectedScenarioId && (
                <TagIconButton
                  icon={<EyeOutlined />}
                  checked={id === selectedScenarioId}
                  onClick={() => handleSetSelectedScenarioId(id)}
                />
              )}
            </>
          ),
        }
      : undefined,
  ].filter(
    (column): column is NonNullable<typeof column> => column !== undefined
  )
  const tableFooter = newReport
    ? () => (
        <NewReportButtonContainer>
          <Button type='primary' icon={<PlusOutlined />} onClick={newReport}>
            Save Report
          </Button>
        </NewReportButtonContainer>
      )
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={ffsReports}
      loading={loading || isSaving}
      footer={tableFooter}
      rowKey='id'
    />
  )
}
